import * as Sentry from '@sentry/browser';
import type { SagaIterator, Task } from 'redux-saga';
import { put, take, fork, call, cancelled, cancel, flush, delay } from 'redux-saga/effects';
import { select as typedSelect } from 'typed-redux-saga';
import { eventChannel } from 'redux-saga';
import io from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import debounce from 'lodash.debounce';
import { getProfile, refreshAccessToken, verifyAccessToken, verifyEmail } from 'flowhawk-core';

import {
  loadMessages,
  loadChatMembers,
  loadPinnedMessages,
  refreshStaleMessages,
  addedToChatroom,
  removedFromChatroom,
  updateChatroomInfo,
  memberRemoved,
  attemptReceiveNewMessage,
  setSocketState,
  triggerChatSocket,
  updateDonorId,
  updateOrganId,
  receiveAcknowledge,
  memberStartedTyping,
  memberStoppedTyping,
  setClearedNotificationCount,
  pinnedChatroom,
  unpinnedChatroom,
  updatePinnedChatrooms,
  removeMemberFromRoom,
  removeChatroom,
  redactMessageSuccess,
  unredactMessageSuccess,
  editMessageSuccess,
  receiveChatroomInfo,
  deleteFileMessageSuccess,
  receiveRoomIconUpdate,
  setLastReconnectedTime,
  receiveChatroomManagers,
} from '../../Redux/ChatListActions';
import {
  getApplicationData,
  setSagaMessage,
  setNextReconnectAttemptIn,
  RECONNECT_DELAYS_SECONDS,
} from '../../Redux/ApplicationActions';
import {
  getDonor,
  getDonorTaskData,
  receiveEditCaseTaskStatus,
  setShouldRefreshCaseList,
  pinnedCase,
  unpinnedCase,
  receiveOrganizationAlert,
} from '../../Redux/DonorActions';
import { offerCreated } from '../../Redux/ChatEditActions';
import emitWithHandlers from './EmitWithHandlers';
import { getCommandChannel, getResultChannel } from './Channels';
import type { SendCommand } from './Commands';
import { commandResult, getCommandMethod } from './Commands';
import TypingTimerManager from './TypingTimerManager';
import { parseMessageStatuses } from '../../Utils/parseMessageStatuses';
import { singleErrorMessage } from '../../Utils/flattenError';
// TODO: Maybe combine this method into a more automatic form of handling from devLog default export:
import devLog, { devLogSocketWarning } from '../../Utils/logger';
import { getFeaturePermissions } from '../../Redux/PermissionActions';
import { refetchChatList } from '../../Utils/hooks';
import { isoNowUTC } from '../../Utils/time';
import type { UserProfile } from '../../Utils/types';
import type {
  ApiApplicationData,
  ApiBaseChatroom,
  ApiCaseServiceAlert,
  ApiInstantMessage,
  ApiSocketAddedUser,
  ApiSocketAddedUsers,
  ApiSocketCandidateEvent,
  ApiSocketCaseTaskEditStatus,
  ApiSocketChatroomUpdated,
  ApiSocketClearedNotifications,
  ApiSocketCreatedChatroom,
  ApiSocketCreatedDonor,
  ApiSocketDeletedChatroom,
  ApiSocketDonorEvent,
  ApiSocketDonorUpdated,
  ApiSocketEventError,
  ApiSocketFileMessageDeleted,
  ApiSocketMemberRemoved,
  ApiSocketMessageEdited,
  ApiSocketMessageRedacted,
  ApiSocketMessageSent,
  ApiSocketMessageStatusSent,
  ApiSocketMessageUnredacted,
  ApiSocketNewMessageAck,
  ApiSocketNewMessageStatus,
  ApiSocketPinnedCase,
  ApiSocketPinnedChatroom,
  ApiSocketRecipientEvent,
  ApiSocketRemovedFromChatroom,
  ApiSocketRemovedMember,
  ApiSocketStoppedTyping,
  ApiSocketTransactionId,
  ApiSocketUnpinnedCase,
  ApiSocketUnpinnedChatroom,
  ApiSocketUpdatedChatroom,
  ApiSocketUserAdded,
  ApiSocketUsersAdded,
  ApiSocketUserTypingEvent,
  ApiSocketRoomIconUpdated,
  ApiSocketUpdatedRoomIcon,
  ApiSocketChatroomManagerUpdated,
} from '../../Utils/apiTypes';
import type { RootState } from '../../Redux';

const debouncedEmitRefreshCaseList = debounce((emit, val) => emit(setShouldRefreshCaseList(val)), 2000);

type User = Pick<
  UserProfile,
  'userId' | 'email' | 'firstName' | 'lastName' | 'accessLevel' | 'license' | 'organizationId' | 'organizationName'
>;

const authenticate = (socket: typeof Socket, token: string) =>
  emitWithHandlers(socket, 'authenticate', { token }, 'authorized', 'unauthorized', true);

const connect = () => {
  const socket = io(import.meta.env.VITE_SOCKET_URL || '', {
    // Disabled because reconnection is handled in sagas
    reconnection: false,
  });

  return new Promise((resolve, reject) => {
    socket.once('connect', () => resolve(socket));
    socket.once('connect_error', (e: unknown) => reject(e));
    socket.once('connect_timeout', (e: unknown) => reject(e));
  });
};

// Define socket signals that we will be listening for
const subscribe = (socket: typeof Socket, user: User) =>
  eventChannel((emit) => {
    const memberId = user.userId;

    // Manager for typing indicator auto-timeout
    const typingTimerManager = new TypingTimerManager();

    /**
     * Generic function for handling socket events with unexpected or missing data in the response.
     */
    const handleUnexpectedData = (errMsg: string, data: any) => {
      devLogSocketWarning(errMsg, data);
      Sentry.captureException(new Error(errMsg), {
        user: {
          id: user.userId.toString(),
          username: `${user.firstName ?? ''} ${user.lastName ?? ''}`,
          email: user.email,
        },
        extra: {
          ...data,
          organization: user.organizationName,
          license: user.license,
          accessLevel: user.accessLevel,
        },
      });
    };

    /**
     * Socket event handlers:
     */
    socket.on('error', (error: string) => {
      devLog('socket', ['error', error]);
      if (error === 'Expired Token') {
        emit(verifyAccessToken('TXP_CHAT_WEB'));
      }
    });

    socket.on('application-data', (data: ApiApplicationData) => {
      if (data?.transaction_uuid) {
        devLog('socket', ['application-data', data]);

        // $FlowFixMe: Since we return an object literal once the typer thinks we always will do that
        emit(commandResult(data.transaction_uuid, data, undefined));
      } else {
        handleUnexpectedData('Socket event `application-data` with unexpected data', data);
      }
    });

    socket.on('get-application-data-error', (error: ApiSocketEventError) => {
      if (error?.transaction_uuid) {
        devLog('socket', ['get-application-data-error', error]);
        emit(commandResult(error.transaction_uuid, undefined, error));
      } else {
        handleUnexpectedData('Socket event `get-application-data-error` with unexpected data', error);
      }
    });

    socket.on('permissions-updated', (data?: { kind?: string; target_type?: string; target_id?: number }) => {
      devLog('socket', ['permissions-updated', data]);
      const kind = data?.kind;
      if (kind === 'orgCaseOverview') {
        // Granting/revoking an org-overview right touches many cases at once.
        emit(setShouldRefreshCaseList(true));
      } else if (kind === 'object' && data?.target_type === 'Donor' && data?.target_id) {
        emit(getDonor(data.target_id));
      } else if (kind === 'feature') {
        emit(getFeaturePermissions());
      }
    });

    socket.on('donor-updated', (data: ApiSocketDonorUpdated) => {
      devLog('socket', ['donor-updated', data]);
      if (data && data.sender_id) {
        // NOTE: This means we also will refetch data for the user that committed the update
        // This is to ensure any other browser windows/devices get the freshest data as well
        emit(setShouldRefreshCaseList(true));
        emit(getDonor(data.donor_id));
        emit(getDonorTaskData(data.donor_id));
      }
    });

    // TODO: Look into the inconsistent response (different if conditionals here) and confirm
    //   this event handler is correct:
    socket.on('added-to-chatroom', (data: ApiBaseChatroom) => {
      devLog('socket', ['added-to-chatroom', data]);
      const chatroomType = data?.chatroom_type || '';
      if (chatroomType === 'Donor Only' || chatroomType === 'Follower Group') {
        debouncedEmitRefreshCaseList(emit, true);
      }
      if (data?.chatroom_id) {
        emit(
          addedToChatroom(
            data.chatroom_id,
            false,
            data.chatroom_name,
            data.chatroom_description,
            data.chatroom_type,
            data.managed || false,
            data.creator_id,
            data.member_count,
            data.total_message_count,
            {
              textContent: data.last_instant_message_content ? data.last_instant_message_content : 'File uploaded',
              sentTime: data.last_instant_message_time,
            },
            data.last_update_time,
            undefined,
            data.donor_id,
            data.organ_id,
            data.follower_id,
            data.create_date,
            data.case_name,
            data.follower_name,
          ),
        );
      } else {
        handleUnexpectedData('Socket event `added-to-chatroom` with unexpected data', data);
      }
    });

    socket.on('message-sent', (data: ApiSocketMessageSent) => {
      if (data?.transaction_uuid && data?.instant_messages) {
        devLog('socket', ['message-sent', data]);

        // $FlowFixMe: Since we return an object literal once the typer thinks we always will do that
        emit(commandResult(data.transaction_uuid, data, undefined));
      } else {
        handleUnexpectedData('Socket event `message-sent` with unexpected data', data);
      }
    });

    socket.on('send-message-error', (error: ApiSocketEventError) => {
      if (error?.transaction_uuid) {
        devLog('socket', ['send-message-error', error]);
        emit(commandResult(error.transaction_uuid, undefined, error));
      } else {
        handleUnexpectedData('Socket event `send-message-error` with unexpected data', error);
      }
    });

    socket.on('added-user-v2', (data: ApiSocketAddedUser) => {
      if (data?.transaction_uuid && data?.chatroom_id) {
        devLog('socket', ['added-user-v2', data]);

        // $FlowFixMe: Since we return an object literal once the typer thinks we always will do that
        emit(commandResult(data.transaction_uuid, data, undefined));
      } else {
        handleUnexpectedData('Socket event `added-user` with unexpected data', data);
      }
    });

    socket.on('added-users', (data: ApiSocketAddedUsers) => {
      if (data?.transaction_uuid && data?.chatroom_id && data?.member_ids) {
        // $FlowFixMe: Since we return an object literal once the typer thinks we always will do that
        emit(commandResult(data.transaction_uuid, data, undefined));
      } else {
        handleUnexpectedData('Socket event `added-users` with unexpected data', data);
      }
    });

    socket.on('not-txp-user', (error: ApiSocketTransactionId) => {
      if (error?.transaction_uuid) {
        // TODO Refactor:
        error.type = 'not-txp-user';
        devLog('socket', ['not-txp-user', error]);
        emit(commandResult(error.transaction_uuid, undefined, error));
      } else {
        handleUnexpectedData('Socket event `not-txp-user` with unexpected data', error);
      }
    });

    socket.on('removed-member-v2', (data: ApiSocketRemovedMember) => {
      if (data?.chatroom_id && data?.member_id) {
        devLog('socket', ['removed-member-v2', data]);

        // $FlowFixMe: Since we return an object literal once the typer thinks we always will do that
        emit(removeMemberFromRoom(data.chatroom_id, data.member_id));
      } else {
        handleUnexpectedData('Socket event `removed-member` with unexpected data', data);
      }
    });

    socket.on('remove-member-error', (error: ApiSocketEventError) => {
      if (error?.transaction_uuid) {
        devLog('socket', ['remove-member-error', error]);
        emit(setSagaMessage('Could not remove member', singleErrorMessage(error), ''));
      } else {
        handleUnexpectedData('Socket event `remove-member-error` with unexpected data', error);
      }
    });

    socket.on('add-user-error', (error: ApiSocketEventError) => {
      if (error?.transaction_uuid) {
        // TODO Refactor:
        error.type = 'add-user-error';
        devLog('socket', ['add-user-error', error]);
        emit(commandResult(error.transaction_uuid, undefined, error));
      } else {
        handleUnexpectedData('Socket event `add-user-error` with unexpected data', error);
      }
    });

    socket.on('removed-from-chatroom', (data: ApiSocketRemovedFromChatroom) => {
      if (data?.chatroom_id) {
        devLog('socket', ['removed-from-chatroom', data]);
        // If the event contains a transaction_uuid, it is a response to 'remove-member' where member is self
        if (data.transaction_uuid) {
          emit(removeChatroom(data.chatroom_id));
        } else {
          // $FlowFixMe: Since we return an object literal once the typer thinks we always will do that
          emit(removedFromChatroom(data.chatroom_id));
        }
      } else {
        handleUnexpectedData('Socket event `removed-from-chatroom` with unexpected data', data);
      }
    });

    socket.on('deleted-chatroom', (data: ApiSocketDeletedChatroom) => {
      if (data?.chatroom_id) {
        devLog('socket', ['deleted-chatroom', data]);
        emit(removeChatroom(data.chatroom_id));
      } else {
        handleUnexpectedData('Socket event `deleted-chatroom` with unexpected data', data);
      }
    });

    socket.on('chatroom-deleted', (data: ApiSocketDeletedChatroom) => {
      if (data?.chatroom_id) {
        devLog('socket', ['chatroom-deleted', data]);
        emit(removeChatroom(data.chatroom_id));
      } else {
        handleUnexpectedData('Socket event `chatroom-deleted` with unexpected data', data);
      }
    });

    socket.on('user-added-v2', (data: ApiSocketUserAdded) => {
      if (data?.chatroom_id && data?.member_id) {
        devLog('socket', ['user-added-v2', data]);

        // Update member count for chatroom
        emit(loadChatMembers(data.chatroom_id));
      } else {
        handleUnexpectedData('Socket event `user-added-v2` with unexpected data', data);
      }
    });

    socket.on('users-added', (data: ApiSocketUsersAdded) => {
      if (data?.chatroom_id && data?.member_ids) {
        devLog('socket', ['Found a users-added event']);
        emit(loadChatMembers(data.chatroom_id));
      } else {
        handleUnexpectedData('Socket event `users-added` with unexpected data', data);
      }
    });

    socket.on('member-removed-v2', (data: ApiSocketMemberRemoved) => {
      if (data?.chatroom_id && data?.member_id) {
        devLog('socket', ['member-removed-v2', data]);

        // Update member count for chatroom
        emit(memberRemoved(data.chatroom_id, data.member_id, data.member_count, false));
      } else {
        handleUnexpectedData('Socket event `member-removed-v2` with unexpected data', data);
      }
    });

    socket.on('new-message', (data: ApiInstantMessage) => {
      if (data?.chatroom_id && data?.instant_message_id && data?.sender_id) {
        devLog('socket', ['new-message', data]);

        // store the message
        emit(
          attemptReceiveNewMessage(
            data.chatroom_id,
            memberId,
            {
              id: data.instant_message_id,
              senderId: data.sender_id,
              senderName: data.sender_name,
              sentTime: data.sent_time,
              messageType: data.chat_type_id,
              textContent: data.text_content || '',
              dataContent: data.data_content,
              displayName: data.display_name,
              fileName: data.file_name,
              presignedUrl: data.presigned_url,
              presignedThumbnailUrl: data.presigned_thumbnail_url,
              seenByCount: data.read_count || 0,
              seenByCurrentUser: data.is_seen_by_current_user || false,

              localPath: undefined,
              status: undefined,
              ack: {},
              redactTime: undefined,
              editTime: undefined,
              replyingMessageId: data.replying_to,
              replyingTextContent: data.replying_to_text_content,
              replyingMessageType: data.replying_to_chat_type_id,
              replyingDataContent: data.replying_to_data_content,
              replyingFilePath: data.replying_to_file_path,
              replyingSenderId: data.replying_to_sender_id,
              parentMessageId: data.parent_message_id,
              caseId: data.case_id,
              highPriority: data.high_priority || false,
            },
            data.instant_message_id,
            'end',
            {
              textContent: data.text_content ? data.text_content : 'File uploaded',
              sentTime: data.sent_time,
            },
            data.sender_id === memberId ? data.sent_time : undefined,
            data.sender_id === memberId ? 1 : 0,
          ),
        );
      } else {
        handleUnexpectedData('Socket event `new-message` with unexpected data', data);
      }
    });

    socket.on('created-donor', (data: ApiSocketCreatedDonor) => {
      if (data?.donor_id && data?.organ_id) {
        devLog('socket', ['created-donor', data]);
        emit(offerCreated(data.donor_id, data.organ_id));
      } else {
        handleUnexpectedData('Socket event `created-donor` with unexpected data', data);
      }
    });

    socket.on('new-donor', (data: ApiSocketDonorEvent) => {
      if (data?.chatroom_id && data?.instant_message_id && data?.data_content) {
        devLog('socket', ['new-donor', data]);

        // store the message
        emit(
          attemptReceiveNewMessage(
            data.chatroom_id,
            memberId,
            {
              id: data.instant_message_id,
              senderId: data.sender_id,
              senderName: '',
              sentTime: data.sent_time,
              messageType: data.chat_type_id,
              textContent: data.text_content,
              dataContent: data.data_content,
              displayName: undefined,
              fileName: undefined,
              seenByCount: data.read_count || 0,
              seenByCurrentUser: false,
              highPriority: data.high_priority || false,

              localPath: undefined,
              status: undefined,
              ack: {},
              redactTime: undefined,
              editTime: undefined,
              replyingMessageId: undefined,
              replyingTextContent: undefined,
              replyingMessageType: undefined,
              replyingDataContent: undefined,
              replyingFilePath: undefined,
              replyingSenderId: undefined,
              parentMessageId: undefined,
              caseId: undefined,
            },
            data.instant_message_id,
            'end',
            {
              textContent: data.text_content,
              sentTime: data.sent_time,
            },
            data.sender_id === memberId ? data.sent_time : undefined,
            data.sender_id === memberId ? 1 : 0,
          ),
        );

        // update donor id
        emit(updateDonorId(data.chatroom_id, data.donor_id));

        // update organ id
        emit(updateOrganId(data.chatroom_id, data.organ_id));
      } else {
        handleUnexpectedData('Socket event `new-donor` with unexpected data', data);
      }
    });

    socket.on('updated-donor', (data: ApiSocketDonorEvent) => {
      if (data?.chatroom_id && data?.instant_message_id && data?.data_content) {
        devLog('socket', ['updated-donor', data]);

        // store the message
        emit(
          attemptReceiveNewMessage(
            data.chatroom_id,
            memberId,
            {
              id: data.instant_message_id,
              senderId: data.sender_id,
              senderName: '',
              sentTime: data.sent_time,
              messageType: data.chat_type_id,
              textContent: data.text_content,
              dataContent: data.data_content,
              displayName: undefined,
              fileName: undefined,
              seenByCount: data.read_count || 0,
              seenByCurrentUser: false,
              highPriority: data.high_priority || false,

              localPath: undefined,
              status: undefined,
              ack: {},
              redactTime: undefined,
              editTime: undefined,
              replyingMessageId: undefined,
              replyingTextContent: undefined,
              replyingMessageType: undefined,
              replyingDataContent: undefined,
              replyingFilePath: undefined,
              replyingSenderId: undefined,
              parentMessageId: undefined,
              caseId: undefined,
            },
            data.instant_message_id,
            'end',
            {
              textContent: data.text_content,
              sentTime: data.sent_time,
            },
            data.sender_id === memberId ? data.sent_time : undefined,
            data.sender_id === memberId ? 1 : 0,
          ),
        );
        emit(loadMessages(data.chatroom_id, memberId, true, 0));
      } else {
        handleUnexpectedData('Socket event `updated-donor` with unexpected data', data);
      }
    });

    socket.on('new-recipient', (data: ApiSocketRecipientEvent) => {
      if (data?.chatroom_id && data?.instant_message_id && data?.data_content) {
        devLog('socket', ['new-recipient', data]);

        // store the message
        emit(
          attemptReceiveNewMessage(
            data.chatroom_id,
            memberId,
            {
              id: data.instant_message_id,
              senderId: data.sender_id,
              senderName: '',
              sentTime: data.sent_time,
              messageType: data.chat_type_id,
              textContent: data.text_content,
              dataContent: data.data_content,
              displayName: undefined,
              fileName: undefined,
              seenByCount: data.read_count || 0,
              seenByCurrentUser: false,
              highPriority: data.high_priority || false,

              localPath: undefined,
              status: undefined,
              ack: {},
              redactTime: undefined,
              editTime: undefined,
              replyingMessageId: undefined,
              replyingTextContent: undefined,
              replyingMessageType: undefined,
              replyingDataContent: undefined,
              replyingFilePath: undefined,
              replyingSenderId: undefined,
              parentMessageId: undefined,
              caseId: undefined,
            },
            data.instant_message_id,
            'end',
            {
              textContent: data.text_content,
              sentTime: data.sent_time,
            },
            data.sender_id === memberId ? data.sent_time : undefined,
            data.sender_id === memberId ? 1 : 0,
          ),
        );
      } else {
        handleUnexpectedData('Socket event `new-recipient` with unexpected data', data);
      }
    });

    socket.on('updated-recipient', (data: ApiSocketRecipientEvent) => {
      if (data?.chatroom_id && data?.instant_message_id && data?.data_content) {
        devLog('socket', ['updated-recipient', data]);

        // store the message
        emit(
          attemptReceiveNewMessage(
            data.chatroom_id,
            memberId,
            {
              id: data.instant_message_id,
              senderId: data.sender_id,
              senderName: '',
              sentTime: data.sent_time,
              messageType: data.chat_type_id,
              textContent: data.text_content,
              dataContent: data.data_content,
              displayName: undefined,
              fileName: undefined,
              seenByCount: data.read_count || 0,
              seenByCurrentUser: false,
              highPriority: data.high_priority || false,

              localPath: undefined,
              status: undefined,
              ack: {},
              redactTime: undefined,
              editTime: undefined,
              replyingMessageId: undefined,
              replyingTextContent: undefined,
              replyingMessageType: undefined,
              replyingDataContent: undefined,
              replyingFilePath: undefined,
              replyingSenderId: undefined,
              parentMessageId: undefined,
              caseId: undefined,
            },
            data.instant_message_id,
            'end',
            {
              textContent: data.text_content,
              sentTime: data.sent_time,
            },
            data.sender_id === memberId ? data.sent_time : undefined,
            data.sender_id === memberId ? 1 : 0,
          ),
        );
        emit(loadMessages(data.chatroom_id, memberId, true, 0));
      } else {
        handleUnexpectedData('Socket event `updated-recipient` with unexpected data', data);
      }
    });

    socket.on('new-candidate', (data: ApiSocketCandidateEvent) => {
      if (data?.chatroom_id && data?.instant_message_id && data?.data_content) {
        devLog('socket', ['new-candidate', data]);

        // store the message
        emit(
          attemptReceiveNewMessage(
            data.chatroom_id,
            memberId,
            {
              id: data.instant_message_id,
              senderId: data.sender_id,
              senderName: '',
              sentTime: data.sent_time,
              messageType: data.chat_type_id,
              textContent: data.text_content,
              dataContent: data.data_content,
              displayName: undefined,
              fileName: undefined,
              seenByCount: data.read_count || 0,
              seenByCurrentUser: false,
              highPriority: data.high_priority || false,

              localPath: undefined,
              status: undefined,
              ack: {},
              redactTime: undefined,
              editTime: undefined,
              replyingMessageId: undefined,
              replyingTextContent: undefined,
              replyingMessageType: undefined,
              replyingDataContent: undefined,
              replyingFilePath: undefined,
              replyingSenderId: undefined,
              parentMessageId: undefined,
              caseId: undefined,
            },
            data.instant_message_id,
            'end',
            {
              textContent: data.text_content,
              sentTime: data.sent_time,
            },
            data.sender_id === memberId ? data.sent_time : undefined,
            data.sender_id === memberId ? 1 : 0,
          ),
        );
      } else {
        handleUnexpectedData('Socket event `new-candidate` with unexpected data', data);
      }
    });

    socket.on('updated-candidate', (data: ApiSocketCandidateEvent) => {
      if (data?.chatroom_id && data?.instant_message_id && data?.data_content) {
        devLog('socket', ['updated-candidate', data]);

        // store the message
        emit(
          attemptReceiveNewMessage(
            data.chatroom_id,
            memberId,
            {
              id: data.instant_message_id,
              senderId: data.sender_id,
              senderName: '',
              sentTime: data.sent_time,
              messageType: data.chat_type_id,
              textContent: data.text_content,
              dataContent: data.data_content,
              displayName: undefined,
              fileName: undefined,
              seenByCount: data.read_count || 0,
              seenByCurrentUser: false,
              highPriority: data.high_priority || false,

              localPath: undefined,
              status: undefined,
              ack: {},
              redactTime: undefined,
              editTime: undefined,
              replyingMessageId: undefined,
              replyingTextContent: undefined,
              replyingMessageType: undefined,
              replyingDataContent: undefined,
              replyingFilePath: undefined,
              replyingSenderId: undefined,
              parentMessageId: undefined,
              caseId: undefined,
            },
            data.instant_message_id,
            'end',
            {
              textContent: data.text_content,
              sentTime: data.sent_time,
            },
            data.sender_id === memberId ? data.sent_time : undefined,
            data.sender_id === memberId ? 1 : 0,
          ),
        );
        emit(loadMessages(data.chatroom_id, memberId, true, 0));
      } else {
        handleUnexpectedData('Socket event `updated-candidate` with unexpected data', data);
      }
    });

    socket.on('new-message-status', (data: ApiSocketNewMessageStatus) => {
      devLog('socket', ['new-message-status', data]);
      // chatId should really be in the response header, but we have to extract it from message details
      let chatId = 0;
      let readerId = 0;
      if (data?.instant_message_status_list[0]) {
        chatId = data.instant_message_status_list[0].chatroom_id;
        readerId = data.instant_message_status_list[0].member_id;
      }

      const statusAction = parseMessageStatuses(memberId, chatId, data, false);

      if (statusAction) {
        // If this has come from current user on another device then update counts
        if (readerId === memberId) {
          statusAction.newStatusCount = data.new_status_count;
          statusAction.updatedStatusCount = data.updated_status_count;
        }
        emit(statusAction);
      } else {
        handleUnexpectedData('Socket event `new-message-status` with unexpected data', data);
      }
    });

    socket.on('message-status-sent', (data: ApiSocketMessageStatusSent) => {
      if (data?.transaction_uuid) {
        devLog('socket', ['message-status-sent', data]);

        // $FlowFixMe: Since we return an object literal once the typer thinks we always will do that
        emit(commandResult(data.transaction_uuid, data, undefined));
      } else {
        handleUnexpectedData('Socket event `message-status-sent` with unexpected data', data);
      }
    });

    socket.on('message-status-error', (error: ApiSocketEventError) => {
      if (error?.transaction_uuid) {
        devLog('socket', ['message-status-error', error]);
        emit(commandResult(error.transaction_uuid, undefined, error));
      } else {
        handleUnexpectedData('Socket event `message-status-error` with unexpected data', error);
      }
    });

    socket.on('message-ack-sent', (data: ApiSocketTransactionId) => {
      if (data?.transaction_uuid) {
        devLog('socket', ['message-ack-sent', data]);

        // $FlowFixMe: Since we return an object literal once the typer thinks we always will do that
        emit(commandResult(data.transaction_uuid, data, undefined));
      } else {
        handleUnexpectedData('Got Socket event `message-ack-sent` with unexpected data', data);
      }
    });

    socket.on('message-ack-error', (error: ApiSocketEventError) => {
      if (error?.transaction_uuid) {
        devLog('socket', ['messages-ack-error', error]);
        emit(commandResult(error.transaction_uuid, undefined, error));
      } else {
        handleUnexpectedData('Got Socket event `ack-message-error` with unexpected data', error);
      }
    });

    socket.on('new-message-ack', (data: ApiSocketNewMessageAck) => {
      if (data?.chatroom_id && data?.instant_message_id) {
        devLog('socket', ['new-message-ack', data]);
        emit(
          receiveAcknowledge(
            data.chatroom_id,
            data.instant_message_id,
            data.chatroom_member_id,
            `0x${data.symbol.substring(2)}`,
          ),
        );
      } else {
        handleUnexpectedData('Got Socket event `new-message-ack` with unexpected data', data);
      }
    });

    socket.on('message-redacted', (data: ApiSocketMessageRedacted) => {
      if (data && data.chatroom_id && data.instant_message_id && data.redact_time) {
        devLog('socket', ['message-redacted', data]);
        emit(redactMessageSuccess(data.chatroom_id, data.instant_message_id, data.redact_time));
      } else {
        handleUnexpectedData('Got Socket event `message-redacted` with unexpected data', data);
      }
    });

    socket.on('file-message-deleted', (data: ApiSocketFileMessageDeleted) => {
      if (data?.chatroom_id && data?.instant_message_id) {
        devLog('socket', ['file-message-deleted', data]);
        emit(deleteFileMessageSuccess(data.chatroom_id, data.instant_message_id));
      } else {
        handleUnexpectedData('Got Socket event `file-message-deleted` with unexpected data', data);
      }
    });

    socket.on('message-redact-error', (error: ApiSocketEventError) => {
      if (error?.transaction_uuid) {
        devLog('socket', ['message-redact-error', error]);
        emit(setSagaMessage('Failed to redact message', '', ''));
      } else {
        handleUnexpectedData('Got socket event `message-redact-error` with unexpected data', error);
      }
    });

    socket.on('message-unredacted', (data: ApiSocketMessageUnredacted) => {
      if (data?.chatroom_id && data?.instant_message_id) {
        devLog('socket', ['message-unredacted', data]);
        emit(unredactMessageSuccess(data.chatroom_id, data.instant_message_id));
      } else {
        handleUnexpectedData('Got Socket event `message-unredacted` with unexpected data', data);
      }
    });

    socket.on('message-unredact-error', (error: ApiSocketEventError) => {
      if (error?.transaction_uuid) {
        devLog('socket', ['message-unredact-error', error]);
        emit(setSagaMessage('Failed to unredact message', '', ''));
      } else {
        handleUnexpectedData('Got Socket event `message-unredact-error` with unexpected data', error);
      }
    });

    // The editor's own socket receives this too, with a transaction_uuid attached. Both arrive on
    // this same event, so EDIT_SUCCESS is written to be idempotent rather than suppressing either.
    socket.on('message-edited', (data: ApiSocketMessageEdited) => {
      if (data?.chatroom_id && data?.instant_message_id && data?.edit_time && typeof data.text_content === 'string') {
        devLog('socket', ['message-edited', data]);
        emit(
          editMessageSuccess(
            data.chatroom_id,
            data.instant_message_id,
            data.text_content,
            data.edit_time,
            data.edited_by,
          ),
        );
      } else {
        handleUnexpectedData('Got Socket event `message-edited` with unexpected data', data);
      }
    });

    // Unlike the redact errors, the server's reason is surfaced: the five rejection causes
    // (window expired, not the sender, not a text message, redacted, wrong room) are meaningfully
    // different to the user, and the socket path carries no status code to branch on.
    socket.on('edit-message-error', (error: ApiSocketEventError) => {
      if (error?.transaction_uuid) {
        devLog('socket', ['edit-message-error', error]);
        emit(setSagaMessage('Failed to edit message', error.error || '', ''));
      } else {
        handleUnexpectedData('Got Socket event `edit-message-error` with unexpected data', error);
      }
    });

    socket.on('started-typing', (data: ApiSocketTransactionId) => {
      if (data?.transaction_uuid) {
        devLog('socket', ['started-typing', data]);

        // $FlowFixMe: Since we return an object literal once the typer thinks we always will do that
        emit(commandResult(data.transaction_uuid, data, undefined));
      } else {
        handleUnexpectedData('Got Socket event `started-typing` with unexpected data', data);
      }
    });

    socket.on('start-typing-error', (error: ApiSocketEventError) => {
      if (error?.transaction_uuid) {
        devLog('socket', ['start-typing-error', error]);
        emit(commandResult(error.transaction_uuid, undefined, error));
      } else {
        handleUnexpectedData('Got socket event `start-typing-error` with unexpected data:', error);
      }
    });

    socket.on('stopped-typing', (data: ApiSocketStoppedTyping) => {
      if (data?.transaction_uuid) {
        devLog('socket', ['stopped-typing', data]);

        // $FlowFixMe: Since we return an object literal once the typer thinks we always will do that
        emit(commandResult(data.transaction_uuid, data, undefined));
      } else {
        handleUnexpectedData('Got Socket event `stopped-typing` with unexpected data', data);
      }
    });

    socket.on('stop-typing-error', (error: ApiSocketEventError) => {
      if (error?.transaction_uuid) {
        devLog('socket', ['stopped-typing-error', error]);
        emit(commandResult(error.transaction_uuid, undefined, error));
      } else {
        handleUnexpectedData('Got Socket event `stop-typing-error` with unexpected data:', error);
      }
    });

    socket.on('user-started-typing', (data: ApiSocketUserTypingEvent) => {
      if (data?.chatroom_id && data?.user_id) {
        devLog('socket', ['user-started-typing', data]);
        emit(memberStartedTyping(data.chatroom_id, data.user_id));

        // Start auto-timeout timer for this typing indicator
        typingTimerManager.startTimer(data.chatroom_id, data.user_id, () => {
          emit(memberStoppedTyping(data.chatroom_id, data.user_id));
        });
      } else {
        handleUnexpectedData('Got Socket event `user-started-typing` with unexpected data', data);
      }
    });

    socket.on('user-stopped-typing', (data: ApiSocketUserTypingEvent) => {
      if (data?.chatroom_id && data?.user_id) {
        devLog('socket', ['user-stopped-typing', data]);
        emit(memberStoppedTyping(data.chatroom_id, data.user_id));

        // Clear the typing timer since we received the stop event
        typingTimerManager.stopTimer(data.chatroom_id, data.user_id);
      } else {
        handleUnexpectedData('Got Socket event `user-stopped-typing` with unexpected data', data);
      }
    });

    socket.on('pinned-chatroom', (data: ApiSocketPinnedChatroom) => {
      if (data?.chatroom_id && data?.pin_number) {
        devLog('socket', ['pinned-chatroom', data]);
        emit(pinnedChatroom(data.chatroom_id, data.pin_number));
      } else {
        handleUnexpectedData('Got Socket event `pinned-chatroom` with unexpected data', data);
      }
    });

    socket.on('pin-chatroom-error', (error: ApiSocketEventError) => {
      if (error?.transaction_uuid) {
        devLog('socket', ['pin-chatroom-error', error]);
        emit(setSagaMessage('Failed to pin chatroom', '', ''));
      } else {
        handleUnexpectedData('Got socket event `pin-chatroom-error` with unexpected data:', error);
      }
    });

    socket.on('unpinned-chatroom', (data: ApiSocketUnpinnedChatroom) => {
      if (data?.chatroom_id) {
        devLog('socket', ['unpinned-chatroom', data]);
        if (data.pinned_chatrooms) {
          emit(updatePinnedChatrooms(data.pinned_chatrooms));
        }
        emit(unpinnedChatroom(data.chatroom_id));
      } else {
        handleUnexpectedData('Got Socket event unpinned-chatroom with unexpected data', data);
      }
    });

    socket.on('pinned-case', (data: ApiSocketPinnedCase) => {
      if (data?.case_id && data?.pin_number) {
        devLog('socket', ['pinned-case', data]);
        emit(pinnedCase(data.case_id, data.pin_number));
      } else {
        handleUnexpectedData('Got Socket event `pinned-case` with unexpected data', data);
      }
    });

    socket.on('unpinned-case', (data: ApiSocketUnpinnedCase) => {
      if (data?.case_id) {
        devLog('socket', ['unpinned-case', data]);
        emit(unpinnedCase(data.case_id));
      } else {
        handleUnexpectedData('Got Socket event unpinned-case with unexpected data', data);
      }
    });

    socket.on('pin-case-error', (error: ApiSocketEventError) => {
      if (error?.transaction_uuid) {
        devLog('socket', ['pin-case-error', error]);
        emit(setSagaMessage('Failed to pin case', '', ''));
      } else {
        handleUnexpectedData('Got socket event `pin-case-error` with unexpected data:', error);
      }
    });

    socket.on('unpin-case-error', (error: ApiSocketEventError) => {
      if (error?.transaction_uuid) {
        devLog('socket', ['unpin-case-error', error]);
        emit(setSagaMessage('Failed to unpin case', '', ''));
      } else {
        handleUnexpectedData('Got socket event `unpin-case-error` with unexpected data:', error);
      }
    });

    socket.on('unpin-chatroom-error', (error: ApiSocketEventError) => {
      if (error?.transaction_uuid) {
        devLog('socket', ['unpin-chatroom-error', error]);
        emit(setSagaMessage('Failed to unpin chatroom', '', ''));
      } else {
        handleUnexpectedData('Got socket event unpin-chatroom-error with unexpected data:', error);
      }
    });

    socket.on('message-pinned', (data: any) => {
      if (data?.chatroom_id && data?.instant_message_id && data?.pin_order) {
        devLog('socket', ['message-pinned', data]);
        // reload the full pinned message data
        emit(loadPinnedMessages(data.chatroom_id));
      } else {
        handleUnexpectedData('Got socket event `message-pinned` with unexpected data:', data);
      }
    });

    socket.on('pin-message-error', (error: ApiSocketEventError) => {
      if (error?.transaction_uuid) {
        devLog('socket', ['pin-message-error', error]);
        emit(setSagaMessage('Failed to pin message', '', ''));
      } else {
        handleUnexpectedData('Got socket event `pin-message-error` with unexpected data:', error);
      }
    });

    socket.on('message-unpinned', (data: any) => {
      if (data?.chatroom_id && data?.instant_message_id) {
        devLog('socket', ['message-unpinned', data]);
        // reload pinned messages data
        emit(loadPinnedMessages(data.chatroom_id));
      } else {
        handleUnexpectedData('Got socket event `message-unpinned` with unexpected data:', data);
      }
    });

    socket.on('unpin-message-error', (error: ApiSocketEventError) => {
      if (error?.transaction_uuid) {
        devLog('socket', ['unpin-message-error', error]);
        emit(setSagaMessage('Failed to unpin message', '', ''));
      } else {
        handleUnexpectedData('Got socket event `unpin-message-error` with unexpected data:', error);
      }
    });

    socket.on('created-chatroom', (data: ApiSocketCreatedChatroom) => {
      refetchChatList();
      devLog('socket', ['created-chatroom', data]);
      if (data?.chatroom_id) {
        // NOTE: transaction_uuid set for events emitted back to client that created the room
        if (data.transaction_uuid) {
          emit(commandResult(data.transaction_uuid, data, undefined));
        } else {
          emit(
            receiveChatroomInfo(
              data.chatroom_id,
              data.chatroom_name,
              data.chatroom_description,
              data.chatroom_type,
              data.managed || false,
              data.instant_message.sender_id,
              1,
              0,
              1,
              data.instant_message.sender_id === memberId ? 1 : 0,
              0,
              0,
              {
                textContent: data.instant_message.text_content || '',
                sentTime: data.instant_message.sent_time,
              },
              data.last_update_time,
              data.last_update_time,
              data.donor_id,
              data.organ_id,
              undefined,
              data.follower_id,
              data.create_date,
              data.case_name,
              data.follower_name,
            ),
          );
        }
      } else {
        handleUnexpectedData('Got Socket event `created-chatroom` with unexpected data', data);
      }
    });

    socket.on('create-chatroom-error', (error: ApiSocketEventError) => {
      if (error?.transaction_uuid) {
        devLog('socket', ['create-chatroom-error', error]);
        emit(commandResult(error.transaction_uuid, undefined, error));
      } else {
        handleUnexpectedData('Got Socket event `create-chatroom-error` with unexpected data', error);
      }
    });

    socket.on('update-chatroom-error', (error: ApiSocketEventError) => {
      if (error?.transaction_uuid) {
        devLog('socket', ['update-chatroom-error', error]);
        emit(commandResult(error.transaction_uuid, undefined, error));
      } else {
        handleUnexpectedData('Got Socket event `update-chatroom-error` with unexpected data', error);
      }
    });

    socket.on('chatroom-updated', (data: ApiSocketChatroomUpdated) => {
      if (data?.chatroom_id && data?.chatroom_name) {
        devLog('socket', ['chatroom-updated', data]);
        emit(
          updateChatroomInfo(
            data.chatroom_id,
            data.chatroom_name,
            data.chatroom_description,
            data.managed,
            data.last_update_time,
          ),
        );
      } else {
        handleUnexpectedData('Got Socket event `chatroom-updated` with unexpected data', data);
      }
    });

    socket.on('updated-room-icon-broadcast', (data: ApiSocketRoomIconUpdated) => {
      if (data?.chatroom_id) {
        devLog('socket', ['updated-room-icon-broadcast', data]);
        emit(receiveRoomIconUpdate(data.chatroom_id, data.room_icon ?? undefined));
      } else {
        handleUnexpectedData('Got Socket event `updated-room-icon-broadcast` with unexpected data', data);
      }
    });

    socket.on('chatroom-manager-updated', (data: ApiSocketChatroomManagerUpdated) => {
      if (data?.chatroom_id && Array.isArray(data?.manager_ids)) {
        devLog('socket', ['chatroom-manager-updated', data]);
        emit(receiveChatroomManagers(data.chatroom_id, data.manager_ids));
      } else {
        handleUnexpectedData('Got Socket event `chatroom-manager-updated` with unexpected data', data);
      }
    });

    socket.on('update-room-icon-error', (error: ApiSocketEventError) => {
      if (error?.transaction_uuid) {
        devLog('socket', ['update-room-icon-error', error]);
        emit(commandResult(error.transaction_uuid, undefined, error));
      } else {
        handleUnexpectedData('Got Socket event `update-room-icon-error` with unexpected data', error);
      }
    });

    socket.on('updated-room-icon', (data: ApiSocketUpdatedRoomIcon) => {
      if (data?.chatroom_id && data?.transaction_uuid) {
        devLog('socket', ['updated-room-icon', data]);
        emit(commandResult(data.transaction_uuid, data, undefined));
      } else {
        handleUnexpectedData('Got Socket event `updated-room-icon` with unexpected data', data);
      }
    });

    socket.on('updated-chatroom', (data: ApiSocketUpdatedChatroom) => {
      if (data?.chatroom_id && Number.isInteger(data.chatroom_id) && data?.transaction_uuid) {
        devLog('socket', ['updated-chatroom', data]);

        // $FlowFixMe: Since we return an object literal once the typer thinks we always will do that
        emit(commandResult(data.transaction_uuid, data, undefined));
      } else {
        handleUnexpectedData('Got Socket event `updated-chatroom` with unexpected data', data);
      }
    });

    socket.on('cleared-notifications', (data: ApiSocketClearedNotifications) => {
      // handle the case where cleared_count is 0, which is a valid scenario when user clears notifications but there were no notifications to clear
      if (data?.chatroom_id && typeof data?.cleared_count === 'number') {
        devLog('socket', ['cleared-notifications', data]);
        emit(setClearedNotificationCount(data.chatroom_id, data.cleared_count));
      } else {
        handleUnexpectedData('Got Socket event `cleared-notifications` with unexpected data', data);
      }
    });

    socket.on('clear-notifications-error', (error: ApiSocketEventError) => {
      if (error) {
        devLog('socket', ['clear-notifications-error', error]);
        emit(setSagaMessage('Failed to clear notifications', singleErrorMessage(error), ''));
      } else {
        handleUnexpectedData('Got Socket event `clear-notifications-error` with unexpected data', error);
      }
    });

    socket.on('notification-resent', (data: ApiSocketTransactionId) => {
      if (data?.transaction_uuid) {
        devLog('socket', ['notification-resent', data]);

        // $FlowFixMe: Since we return an object literal once the typer thinks we always will do that
        emit(commandResult(data.transaction_uuid, data, undefined));
      } else {
        handleUnexpectedData('Got Socket event `notification-resent` with unexpected data', data);
      }
    });
    socket.on('resend-notification-error', (error: ApiSocketEventError) => {
      if (error?.transaction_uuid) {
        devLog('socket', ['resend-notification-error', error]);
        emit(commandResult(error.transaction_uuid, undefined, error));
      } else {
        handleUnexpectedData('Got Socket event `notification-resent-error` with unexpected data', error);
      }
    });

    socket.on('verified-email', () => {
      devLog('socket', ['verified-email']);
      emit(verifyEmail());
    });

    socket.on('case-task-edit-status', (data: ApiSocketCaseTaskEditStatus) => {
      devLog('socket', ['case-task-edit-status', data]);
      emit(receiveEditCaseTaskStatus(data.case_id, data.task_id, data.edit_by, data.edit_time));
    });

    socket.on('case-alert', (data: Partial<ApiCaseServiceAlert>) => {
      devLog('socket', ['case-alert', data]);
      emit(receiveOrganizationAlert(data.caseId ?? 0, data.id ?? 0, data));
    });

    socket.on('organization-membership-approved', () => {
      devLog('socket', ['organization-membership-approved']);
      // Refresh the access token so that our new accessLevel of Organization Member is reflected
      emit(refreshAccessToken('TXP_CHAT_WEB'));
      emit(getProfile());
    });

    socket.on('profile-updated', () => {
      devLog('socket', ['profile-updated']);
      // Our profile changed on one of this user's other devices (notification settings,
      // do not disturb, alert sounds). Re-read it.
      emit(getProfile());
    });

    socket.on('disconnect', () => {
      devLog('socket', ['disconnect']);

      // Set state to error and trigger reconnect
      emit(setSocketState('error'));
      emit(triggerChatSocket());
    });

    function offlineHandler() {
      devLog('socket', ['Network status changed to offline. Disconnecting websocket.']);
      socket.disconnect();
    }

    // listen for the "offline" event and immediately disconnect socket
    global.addEventListener('offline', offlineHandler);

    return () => {
      // Clear all typing timers
      typingTimerManager.clearAllTimers();

      // Clear event handlers
      socket.off('application-data');
      socket.off('get-application-data-error');
      socket.off('donor-updated');
      socket.off('permissions-updated');
      socket.off('added-to-chatroom');
      socket.off('removed-from-chatroom');
      socket.off('add-user-error');
      socket.off('new-message');
      socket.off('new-message-status');
      socket.off('user-added-v2');
      socket.off('member-removed-v2');
      socket.off('chatroom-updated');
      socket.off('updated-chatroom');
      socket.off('update-chatroom-error');
      socket.off('room-icon-updated');
      socket.off('updated-room-icon');
      socket.off('update-room-icon-error');
      socket.off('disconnect');
      socket.off('message-sent');
      socket.off('send-message-error');
      socket.off('added-user');
      socket.off('not-txp-member');
      socket.off('removed-member-v2');
      socket.off('remove-member-error');
      socket.off('created-chatroom');
      socket.off('create-chatroom-error');
      socket.off('cleared-notifications');
      socket.off('clear-notifications-error');
      socket.off('notification-resent');
      socket.off('resend-notification-error');
      socket.off('created-donor');
      socket.off('new-donor');
      socket.off('updated-donor');
      socket.off('new-recipient');
      socket.off('updated-recipient');
      socket.off('new-candidate');
      socket.off('updated-candidate');
      socket.off('message-status-sent');
      socket.off('message-status-error');
      socket.off('message-ack-sent');
      socket.off('message-ack-error');
      socket.off('new-message-ack');
      socket.off('verified-email');
      socket.off('start-typing-error');
      socket.off('stop-typing-error');
      socket.off('started-typing');
      socket.off('start-typing-error');
      socket.off('stopped-typing');
      socket.off('stop-typing-error');
      socket.off('user-started-typing');
      socket.off('user-stopped-typing');
      socket.off('pinned-chatroom');
      socket.off('pin-chatroom-error');
      socket.off('unpinned-chatroom');
      socket.off('unpin-chatroom-error');
      socket.off('message-pinned');
      socket.off('pin-message-error');
      socket.off('message-unpinned');
      socket.off('unpin-message-error');
      socket.off('pinned-case');
      socket.off('pin-case-error');
      socket.off('unpinned-case');
      socket.off('unpin-case-error');
      socket.off('users-added');
      socket.off('added-users');
      socket.off('case-alert');
      socket.off('organization-membership-approved');
      socket.off('profile-updated');

      global.removeEventListener('offline', offlineHandler);
    };
  });

function* read(socket: typeof Socket, user: User): SagaIterator<void> {
  // Subscribe to all events
  const channel = yield call(subscribe, socket, user);
  while (true) {
    const action = yield take(channel);
    yield put(action);
  }
}

function* write(socket: typeof Socket, memberId: number) {
  while (true) {
    const cmdChannel = getCommandChannel();

    if (cmdChannel) {
      const action: SendCommand = yield take(cmdChannel);
      const method = getCommandMethod(action.command || '');

      if (method) {
        // Check if it's a case command and use appropriate ID
        const isCaseCommand = action.command === 'pin-case' || action.command === 'unpin-case';
        const id = isCaseCommand ? action.caseId : action.chatId;
        yield method(socket, id || -1, memberId, action.transactionId, ...(action.params || []));
      }
    } else {
      yield delay(300);
    }
  }
}

function* handleIO(socket: typeof Socket, user: User): SagaIterator<void> {
  const readTask = yield fork(read, socket, user);
  const writeTask = yield fork(write, socket, user.userId);
  try {
    while (true) {
      // Keep this task active by delaying for a long time so we can cascade the cancel
      yield delay(1000000);
    }
  } finally {
    if (yield cancelled()) {
      yield cancel(readTask);
      yield cancel(writeTask);
    }
  }
}

// Whether this session has already had a successful socket connection, used to tell a genuine
// reconnect from the session's first connect.
let hasConnectedThisSession = false;

function* protocolHandler(token: string, user: User): SagaIterator<void> {
  let socket: typeof Socket | undefined;
  let ioTask: Task | undefined;
  let connectionAttempts = 0;

  try {
    // Set state to connecting
    yield put(setSocketState('connecting'));

    // Attempt to connect
    try {
      socket = yield call(connect);
    } catch (firstError) {
      devLog('socket', ['First Connection attempt failed.', firstError]);

      try {
        Sentry.captureException(firstError, {
          user: {
            id: user.userId.toString(),
            username: `${user.firstName ?? ''} ${user.lastName ?? ''}`,
            email: user.email,
          },
          extra: { errorMsg: '[Websocket] First Connect Failed' },
        });
        // Retry once - for genuine network jitters and to allow for network changed errors (occur after reconnecting too quickly)
        // 3000ms is empirically derived through testing - may need to be revised
        yield delay(3000);
        socket = yield call(connect);
      } catch (secondError) {
        devLog('socket', ['Second Connection attempt failed.', secondError]);
        Sentry.captureException(secondError, {
          user: {
            id: user.userId.toString(),
            username: `${user.firstName ?? ''} ${user.lastName ?? ''}`,
            email: user.email,
          },
          extra: { errorMsg: '[Websocket] Second Connect Failed' },
        });
        yield put(setSocketState('error', 'generic'));

        while (true) {
          const delayIndex = Math.min(connectionAttempts, RECONNECT_DELAYS_SECONDS.length - 1);
          const delaySeconds = RECONNECT_DELAYS_SECONDS[delayIndex];
          // add slight jitter to reconnect timing (+- 100ms)
          const delayMs = delaySeconds * 1000;
          const jitterMs = Math.floor(Math.random() * 200) - 100;

          devLog('socket', [`Connection attempt ${connectionAttempts} failed. Retrying in ${delaySeconds}s...`]);
          yield put(setNextReconnectAttemptIn(delaySeconds));
          yield delay(delayMs + jitterMs);

          try {
            devLog('socket', [`Attempting to connect (attempt ${connectionAttempts + 1})...`]);
            socket = yield call(connect);
            devLog('socket', ['Socket connected successfully.']);
            connectionAttempts = 0;
            break;
          } catch (error) {
            devLog('socket', [`Connection attempt ${connectionAttempts} failed.`, error]);
            connectionAttempts += 1;
            Sentry.captureException(error, {
              user: {
                id: user.userId.toString(),
                username: `${user.firstName ?? ''} ${user.lastName ?? ''}`,
                email: user.email,
              },
              extra: {
                errorMsg: '[Websocket] Connect Failed',
                attempt: connectionAttempts,
              },
            });
          }
        }
      }
    }

    // Typescript check: ensure socket is initialized
    if (!socket) {
      throw new Error('Socket is not initialized');
    }

    // Authenticate socket connection
    try {
      yield call(authenticate, socket, token);
    } catch (error) {
      Sentry.captureException(error, { extra: { errorMsg: '[Websocket] Auth Failed' } });
      yield put(setSocketState('error', 'auth_failed'));
      yield put(setNextReconnectAttemptIn(undefined)); // reconnect count cleared
      yield put(refreshAccessToken('TXP_CHAT_WEB'));
      return;
    }

    // Clear any residual "Network unavailable" messages
    const sagaHeading = yield* typedSelect((state: RootState) => state.application.sagaHeading);
    if (sagaHeading && (sagaHeading === 'Network unavailable' || sagaHeading === 'Network unavailable - Retrying')) {
      yield put(setSagaMessage('', '', ''));
    }
    // Ensure reconnect count is cleared
    if (connectionAttempts > 0) {
      yield put(setNextReconnectAttemptIn(undefined));
    }

    // Start io handler in background
    ioTask = yield fork(handleIO, socket, user);
    yield put(setSocketState('connected')); // Successfully connected and authenticated

    const isReconnect = hasConnectedThisSession;
    const activeChatId = yield* typedSelect((state: RootState) => state.chatList.activeChatId);
    if (isReconnect) {
      // Other data fetched before this reconnect may also be stale, since updates could have
      // been missed while disconnected. The active chatroom was already refreshed above; for
      // everything else, mark it for refresh rather than eagerly refetching it now:
      // - Room list: invalidate the react-query cache so it refetches next render.
      refetchChatList();
      // - Case list: flag it for refresh; CaseList watches this and reloads if mounted, or on next mount.
      yield put(setShouldRefreshCaseList(true));
      // - Cached room messages: stamp the reconnect time so openChatroomSaga treats any room
      //   whose messages were cached before this moment as stale and refreshes it when next opened.
      yield put(setLastReconnectedTime(isoNowUTC()));
    }
    if (activeChatId && isReconnect) {
      // Reconnecting can leave a gap where messages broadcast to the active chat while
      // disconnected were never delivered. Re-fetch that chatroom so nothing is missed.
      // Skipped on the session's first connect in order to prevent unnecessary double fetches.
      yield put(loadChatMembers(activeChatId));
      yield put(refreshStaleMessages(activeChatId, user.userId, 0));
    }
    hasConnectedThisSession = true;
    // Re-read the profile on every connect, not just reconnects. A page refresh restores the
    // profile from sessionStorage without contacting the server, so a settings change made on
    // another device while this was closed/disconnected would stay stale.
    yield put(getProfile());
    yield put(getApplicationData(true, false, false, false, []));

    // Wait for events that should trigger socket disconnect
    const action = yield take(['Auth/LOGOUT', 'Application/RESET_DATA', 'Auth/TOKEN_EXPIRED']);

    // Cancel IO to terminate emitting/listening
    if (ioTask) yield cancel(ioTask);

    if (action && (action.type === 'Auth/LOGOUT' || action.type === 'Application/RESET_DATA')) {
      hasConnectedThisSession = false;

      // If logging out, flush all buffers
      const commandChannel = getCommandChannel();
      const resultChannel = getResultChannel();
      if (commandChannel) yield flush(commandChannel);
      if (resultChannel) yield flush(resultChannel);
    }

    yield put(setSocketState('unknown'));
    if (socket) {
      socket.removeAllListeners();
      socket.close();
      socket = undefined;
    }
  } finally {
    if (yield cancelled()) {
      // If socket object exists
      if (socket) {
        // If successfully set, cancel the dependent ioTasks
        if (ioTask) {
          yield cancel(ioTask);
        }

        // Remove all listeners and close socket
        socket.removeAllListeners();
        socket.close();
        socket = undefined;
      }
      yield put(setNextReconnectAttemptIn(undefined)); // Ensure countdown cleared on cancellation
    }
  }
}

export default function* initProtocol(): SagaIterator<void> {
  const token = yield* typedSelect((state: RootState) => state.auth.accessToken);
  const profile = yield* typedSelect((state: RootState) => state.auth.profile);
  if (profile) {
    const { userId, email, firstName, lastName, accessLevel, license, organizationId, organizationName } = profile;
    yield call(protocolHandler, token, {
      userId,
      email,
      firstName,
      lastName,
      accessLevel,
      license,
      organizationId,
      organizationName,
    });
  }
}
